import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onReset?: () => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({
            error,
            errorInfo,
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        if (this.props.onReset) {
            this.props.onReset();
        }
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
                    <Card className="max-w-2xl w-full shadow-xl border-error-200">
                        <CardHeader className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="rounded-full bg-error-100 p-4">
                                    <AlertTriangle className="h-12 w-12 text-error-600" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <CardTitle className="text-2xl text-error-700">
                                    Oops! Something went wrong
                                </CardTitle>
                                <CardDescription className="text-base">
                                    We encountered an unexpected error. Don't worry, your data is safe.
                                </CardDescription>
                            </div>
                        </CardHeader>

                        <CardContent className="space-y-6">
                            {/* Error Details (collapsed by default in production) */}
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <div className="rounded-lg bg-gray-100 p-4 space-y-2">
                                    <p className="text-sm font-semibold text-gray-900">Error Details:</p>
                                    <p className="text-xs text-gray-700 font-mono break-all">
                                        {this.state.error.toString()}
                                    </p>
                                    {this.state.errorInfo && (
                                        <details className="text-xs text-gray-600">
                                            <summary className="cursor-pointer hover:text-gray-900 font-medium">
                                                Stack Trace
                                            </summary>
                                            <pre className="mt-2 overflow-auto max-h-48 p-2 bg-white rounded border border-gray-200">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            )}

                            {/* Recovery Actions */}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button
                                    onClick={this.handleReset}
                                    className="gap-2 min-w-[140px]"
                                    size="lg"
                                >
                                    <RefreshCw className="h-4 w-4" />
                                    Try Again
                                </Button>
                                <Button
                                    onClick={this.handleGoHome}
                                    variant="outline"
                                    className="gap-2 min-w-[140px]"
                                    size="lg"
                                >
                                    <Home className="h-4 w-4" />
                                    Go Home
                                </Button>
                            </div>

                            {/* Help Text */}
                            <div className="text-center text-sm text-muted-foreground">
                                <p>If this problem persists, please contact support.</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
